// src/components/dashboard/DashboardShadcn.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Calendar,
  Users,
  DollarSign,
  TrendingUp,
  Clock,
  FileText,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  Building2,
  Zap
} from 'lucide-react';

// shadcn components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';

// Keep your existing PageContainer for headers
import PageContainer from '../ui/layout/PageContainer/PageContainer';
import LoadingScreen from '../ui/loading/LoadingScreen';

// Keep your existing modal
import CustomerCreationModal from '../Customers/CustomerCreationModal';

// Keep your existing hooks
import { useCustomers } from '../../hooks/useCustomers';
import { useInvoices } from '../../hooks/useInvoices';
import { useEvents } from '../../hooks/useEvents';
import { useTeam } from '../../hooks/useTeam';

import { cn } from '../../utils/cn';

const DashboardShadcn = () => {
  const navigate = useNavigate();

  // Same hooks as before
  const {
    customers,
    customersLoading,
    customersPagination,
    loadCustomers
  } = useCustomers();

  const {
    invoices,
    invoicesLoading,
    invoiceStats,
    loadInvoices,
    loadInvoiceSummary
  } = useInvoices();

  const {
    events,
    appointments,
    eventsLoading,
    loadEvents
  } = useEvents();

  const {
    teamMembers,
    teamLoading,
    loadTeamMembers
  } = useTeam();

  // Same state and handlers as before
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleNewAppointment = () => navigate('/calendar');
  const handleCreateInvoice = () => navigate('/invoices/create');
  const handleAddCustomer = () => setShowCreateModal(true);
  const handleViewAllEvents = () => navigate('/appointments');
  const handleViewAllInvoices = () => navigate('/invoices');
  const handleEventClick = (event) => {
    const eventId = event.id || event.event_id || event._id;
    if (eventId) navigate(`/appointments/${eventId}`);
  };
  const handleInvoiceClick = (invoice) => {
    const invoiceId = invoice.id || invoice.invoice_id || invoice._id;
    if (invoiceId) navigate(`/invoices/edit/${invoiceId}`);
  };
  const handleOverdueAlert = () => {
    navigate('/invoices', { state: { statusFilter: 'overdue' } });
  };

  // Same utility functions
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Same stats calculation
  const dashboardStats = {
    totalCustomers: customersPagination?.total || customers?.length || 0,
    totalRevenue: invoiceStats?.total_revenue || 0,
    pendingRevenue: invoiceStats?.pending_amount || 0,
    upcomingAppointments: appointments?.length || 0,
    completedThisMonth: invoiceStats?.paid_invoices || 0,
    teamMembers: teamMembers?.length || 0
  };

  const recentEvents = events?.slice(0, 4) || [];
  const recentInvoices = invoices?.slice(0, 4) || [];
  const overdueInvoices = invoices?.filter(inv => inv.status === 'overdue') || [];
  const isLoading = customersLoading || invoicesLoading || eventsLoading || teamLoading;

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingScreen
          message="Loading your dashboard..."
          icon={Zap}
          variant="emerald"
          size="lg"
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Stats Grid - Now using shadcn Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">

        {/* Total Customers Card */}
        <Card className="hover:border-emerald-500/30 transition-colors">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-emerald-500/10 rounded-lg">
                <Users className="w-5 h-5 text-emerald-400" />
              </div>
              <TrendingUp className="w-4 h-4 text-emerald-400 opacity-60" />
            </div>
            <div className="text-2xl font-bold">
              {dashboardStats.totalCustomers.toLocaleString()}
            </div>
            <div className="text-sm text-muted-foreground">Total Customers</div>
          </CardContent>
        </Card>

        {/* Total Revenue Card */}
        <Card className="hover:border-blue-500/30 transition-colors">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <DollarSign className="w-5 h-5 text-blue-400" />
              </div>
              <TrendingUp className="w-4 h-4 text-blue-400 opacity-60" />
            </div>
            <div className="text-2xl font-bold">
              {formatCurrency(dashboardStats.totalRevenue)}
            </div>
            <div className="text-sm text-muted-foreground">Total Revenue</div>
          </CardContent>
        </Card>

        {/* Pending Revenue Card */}
        <Card className="hover:border-yellow-500/30 transition-colors">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-yellow-500/10 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
              <AlertCircle className="w-4 h-4 text-yellow-400 opacity-60" />
            </div>
            <div className="text-2xl font-bold">
              {formatCurrency(dashboardStats.pendingRevenue)}
            </div>
            <div className="text-sm text-muted-foreground">Pending Revenue</div>
          </CardContent>
        </Card>

        {/* Upcoming Appointments Card */}
        <Card className="hover:border-purple-500/30 transition-colors">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <Calendar className="w-5 h-5 text-purple-400" />
              </div>
              <Clock className="w-4 h-4 text-purple-400 opacity-60" />
            </div>
            <div className="text-2xl font-bold">
              {dashboardStats.upcomingAppointments}
            </div>
            <div className="text-sm text-muted-foreground">Upcoming</div>
          </CardContent>
        </Card>

        {/* Completed This Month Card */}
        <Card className="hover:border-green-500/30 transition-colors">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <TrendingUp className="w-4 h-4 text-green-400 opacity-60" />
            </div>
            <div className="text-2xl font-bold">
              {dashboardStats.completedThisMonth}
            </div>
            <div className="text-sm text-muted-foreground">Completed</div>
          </CardContent>
        </Card>

        {/* Team Members Card */}
        <Card className="hover:border-indigo-500/30 transition-colors">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-indigo-500/10 rounded-lg">
                <Building2 className="w-5 h-5 text-indigo-400" />
              </div>
              <Users className="w-4 h-4 text-indigo-400 opacity-60" />
            </div>
            <div className="text-2xl font-bold">
              {dashboardStats.teamMembers}
            </div>
            <div className="text-sm text-muted-foreground">Team Members</div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">

        {/* Recent Events - Now using shadcn Card */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-emerald-400" />
                  <CardTitle>Recent Events</CardTitle>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleViewAllEvents}
                  className="text-emerald-400 hover:text-emerald-300"
                >
                  View All
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <CardDescription>Your latest scheduled events</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentEvents.length > 0 ? (
                  recentEvents.map((event, index) => (
                    <div
                      key={event.id || index}
                      onClick={() => handleEventClick(event)}
                      className="flex items-center space-x-4 p-3 rounded-lg border hover:bg-accent cursor-pointer transition-colors"
                    >
                      <div className="w-3 h-3 bg-emerald-400 rounded-full flex-shrink-0"></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{event.title}</p>
                        <p className="text-sm text-muted-foreground truncate">
                          {event.customer_name || 'General Event'}
                        </p>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {formatDate(event.scheduled_datetime)}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-40" />
                    <p className="text-muted-foreground">No recent events</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar with Quick Actions & Alerts */}
        <div className="space-y-6">

          {/* Quick Actions - Now using shadcn Card & Buttons */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                <CardTitle>Quick Actions</CardTitle>
              </div>
              <CardDescription>Common tasks and shortcuts</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                onClick={handleNewAppointment}
                variant="outline"
                className="w-full justify-start text-emerald-300 border-emerald-500/20 hover:bg-emerald-500/10"
              >
                <Calendar className="mr-2 h-4 w-4" />
                New Appointment
              </Button>

              <Button
                onClick={handleCreateInvoice}
                variant="outline"
                className="w-full justify-start text-blue-300 border-blue-500/20 hover:bg-blue-500/10"
              >
                <FileText className="mr-2 h-4 w-4" />
                Create Invoice
              </Button>

              <Button
                onClick={handleAddCustomer}
                variant="outline"
                className="w-full justify-start text-purple-300 border-purple-500/20 hover:bg-purple-500/10"
              >
                <Users className="mr-2 h-4 w-4" />
                Add Customer
              </Button>
            </CardContent>
          </Card>

          {/* Alerts & Notifications */}
          {overdueInvoices.length > 0 && (
            <Card className="border-red-500/30 bg-red-900/10">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                  <CardTitle className="text-red-200">Attention Required</CardTitle>
                </div>
                <CardDescription className="text-red-300/80">
                  Issues that need your attention
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div
                  onClick={handleOverdueAlert}
                  className="flex items-center justify-between p-3 rounded-lg border border-red-500/20 bg-red-500/10 cursor-pointer hover:bg-red-500/20 transition-colors"
                >
                  <div>
                    <div className="text-red-200 font-medium">Overdue Invoices</div>
                    <div className="text-red-300/80 text-sm">
                      {overdueInvoices.length} invoices need attention
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-red-400" />
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Recent Invoices - Now using shadcn Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-400" />
              <CardTitle>Recent Invoices</CardTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleViewAllInvoices}
              className="text-blue-400 hover:text-blue-300"
            >
              View All
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <CardDescription>Your latest invoicing activity</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {recentInvoices.length > 0 ? (
              recentInvoices.map((invoice, index) => (
                <div
                  key={invoice.id || index}
                  onClick={() => handleInvoiceClick(invoice)}
                  className="p-4 rounded-lg border hover:bg-accent cursor-pointer transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium">#{invoice.invoice_number}</div>
                    <Badge
                      variant={
                        invoice.status === 'paid' ? 'default' :
                        invoice.status === 'sent' ? 'secondary' :
                        invoice.status === 'overdue' ? 'destructive' : 'outline'
                      }
                    >
                      {invoice.status}
                    </Badge>
                  </div>
                  <div className="text-muted-foreground text-sm mb-1">
                    {invoice.customer_name}
                  </div>
                  <div className="font-semibold">
                    {formatCurrency(invoice.total_amount)}
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-8">
                <FileText className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-40" />
                <p className="text-muted-foreground">No recent invoices</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Keep the existing modal */}
      <CustomerCreationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={(customerData) => {
          console.log('✅ Customer created successfully from dashboard:', customerData.name);
        }}
      />
    </PageContainer>
  );
};

export default DashboardShadcn;